import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Sign up with email and password
  async function signUp(email, password, metadata = {}) {
    try {
      setError('');
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata
        }
      });
      
      if (error) throw error;
      return data;
    } catch (err) {
      setError('Failed to create account: ' + err.message);
      throw err;
    }
  }

  // Sign in with email and password
  async function login(email, password) {
    try {
      setError('');
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) throw error;
      return data;
    } catch (err) {
      setError('Failed to sign in: ' + err.message);
      throw err;
    }
  }

  // Admin login with admin password
  async function adminLogin(email, password, adminPassword) {
    try {
      setError('');
      
      // Check admin password first
      const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'admin123';
      if (adminPassword !== ADMIN_PASSWORD) {
        throw new Error('Invalid admin password');
      }
      
      // Then authenticate with regular credentials
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) throw error;
      
      // Set admin role in user metadata
      const { error: updateError } = await supabase.auth.updateUser({
        data: { role: 'admin' }
      });
      
      if (updateError) console.warn('Failed to update user role:', updateError);
      
      return data;
    } catch (err) {
      setError('Failed to sign in as admin: ' + err.message);
      throw err;
    }
  }

  // Sign out
  async function logout() {
    try {
      setError('');
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (err) {
      setError('Failed to sign out: ' + err.message);
      throw err;
    }
  }

  // Reset password
  async function resetPassword(email) {
    try {
      setError('');
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });
      
      if (error) throw error;
    } catch (err) {
      setError('Failed to send reset email: ' + err.message);
      throw err;
    }
  }

  // Update password
  async function updatePassword(password) {
    try {
      setError('');
      const { error } = await supabase.auth.updateUser({
        password
      });
      
      if (error) throw error;
    } catch (err) {
      setError('Failed to update password: ' + err.message);
      throw err;
    }
  }

  // Set up auth state observer
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setCurrentUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setCurrentUser(session?.user ?? null);
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const value = {
    currentUser,
    signUp,
    login,
    adminLogin,
    logout,
    resetPassword,
    updatePassword,
    error
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}